/**
 * Centralized enum for all application event types.
 * These event types are used across EventBridge, SQS queues, and the events log table.
 *
 * @remarks
 * - Only add events that are actually implemented
 * - Event types follow the pattern: `{domain}.{action}`
 * - Use lowercase with underscores for consistency with EventBridge
 */
export enum EventType {
	// ─── User Events ──────────────────────────────────────────────────────────
	USER_LOGGED_IN = 'user.logged_in',

	// ─── Auth Events ──────────────────────────────────────────────────────────
	// Emitted when an admin on a non-prod stage mints an impersonation JWT for a
	// target user via POST /auth/dev/impersonate. See ADR-0009 + Spec 0.
	AUTH_IMPERSONATION_STARTED = 'auth.impersonation_started',

	// ─── Entra Directory Events ───────────────────────────────────────────────
	ENTRA_DIRECTORY_CREATED = 'entra_directory.created',
	ENTRA_DIRECTORY_MODIFIED = 'entra_directory.modified',
	ENTRA_DIRECTORY_DISABLED = 'entra_directory.disabled',
	ENTRA_DIRECTORY_REACTIVATED = 'entra_directory.reactivated',
	ENTRA_DIRECTORY_DELETED = 'entra_directory.deleted',

	// ─── Entra Directory Sync Events ──────────────────────────────────────────
	ENTRA_DIRECTORY_SYNC_REQUESTED = 'entra_directory.sync_requested',
	ENTRA_DIRECTORY_SYNC_COMPLETED = 'entra_directory.sync_completed',
	ENTRA_DIRECTORY_SYNC_FAILED = 'entra_directory.sync_failed',

	// ─── Employee Events ──────────────────────────────────────────────────────
	EMPLOYEE_CREATED = 'employee.created',
	EMPLOYEE_UPDATED = 'employee.updated',
	EMPLOYEE_DELETED = 'employee.deleted',

	// ─── Notification Events ──────────────────────────────────────────────────
	NOTIFICATION_EMAIL_REQUESTED = 'notification.email_requested',
	NOTIFICATION_EMAIL_SENT = 'notification.email_sent',
	NOTIFICATION_EMAIL_FAILED = 'notification.email_failed',
	NOTIFICATION_TEAMS_REQUESTED = 'notification.teams_requested',
	NOTIFICATION_TEAMS_SENT = 'notification.teams_sent',
	NOTIFICATION_TEAMS_FAILED = 'notification.teams_failed',

	// ─── IDM Events ──────────────────────────────────────────────────────────
	IDM_GROUP_CREATED = 'idm_group.created',
	IDM_GROUP_USER_ADDED = 'idm_group.user_added',
	IDM_GROUP_USER_REMOVED = 'idm_group.user_removed',
	IDM_REQUEST_RECEIVED = 'idm_request.received',
	IDM_REQUEST_CONFIRMATION_PENDING = 'idm_request.confirmation_pending',
	IDM_REQUEST_CONFIRMED = 'idm_request.confirmed',
	IDM_REQUEST_COMPLETED = 'idm_request.completed',
	IDM_REQUEST_FAILED = 'idm_request.failed',
	/**
	 * Generic "create a Productive ticket" event. Emitted by IDM flows
	 * (`idm-execution-worker`) AND by Request Center final approvals
	 * (Spec 2). Consumed by `ticket-service`'s `ticket-worker`, which
	 * routes on `data.source` to the right Productive project mapping.
	 */
	TICKET_REQUESTED = 'ticket.requested',

	// ─── Tickets Sync Events (Spec 3 Group 8) ─────────────────────────────────
	// `TICKETS_SYNC_REQUESTED` is the kick-off event — published by the cron
	// handler (every 6h) AND by the manual-trigger HTTP endpoint
	// (`POST /tickets/sync`). The `ticket-sync-worker` Lambda subscribes,
	// calls `TicketSyncExecutor.run(jobId)`, and emits `_COMPLETED` /
	// `_FAILED` when done. Plural ("tickets") because the run scopes a list
	// of open tickets, not a single one — distinct from `TICKET_REQUESTED`
	// which IS per-ticket.
	TICKETS_SYNC_REQUESTED = 'tickets.sync_requested',
	TICKETS_SYNC_COMPLETED = 'tickets.sync_completed',
	TICKETS_SYNC_FAILED = 'tickets.sync_failed',

	// ─── Request Center Events ────────────────────────────────────────────────
	// Lifecycle (one event per state-changing action; see `request-center-event-emissions`
	// spec for emission sites + `audit-event-envelope` for the envelope contract).
	REQUEST_SUBMITTED = 'request.submitted',
	REQUEST_STATE_CHANGED = 'request.state_changed',
	REQUEST_APPROVED = 'request.approved',
	REQUEST_REJECTED = 'request.rejected',
	REQUEST_CANCELLED = 'request.cancelled',
	/**
	 * Driven by the `request-center-worker` aggregator: fires when every
	 * ticket linked to the request is closed. Distinct from
	 * `REQUEST_APPROVED` (final-step approval lands in the `approved` state;
	 * `REQUEST_COMPLETED` only fires once provisioning is actually done).
	 */
	REQUEST_COMPLETED = 'request.completed',
	REQUEST_FAILED = 'request.failed',
	REQUEST_FILE_ATTACHED = 'request.file_attached',
	REQUEST_INFO_GATE_ANSWERED = 'request.info_gate_answered',
	/**
	 * `request.info_requested` — an approver clicked "Request more info"
	 * on an active approval row. The workflow status flips to PENDING_INFO
	 * and the approval's SLA pauses until the submitter answers (or the
	 * approver/admin withdraws — `REQUEST_INFO_CANCELLED`).
	 *
	 * Payload: `{ approvalId, infoGateId, fields, note?, requestedByUserId }`.
	 */
	REQUEST_INFO_REQUESTED = 'request.info_requested',
	/**
	 * `request.info_cancelled` — the open info-request was withdrawn by
	 * the approver or an admin without the submitter answering. The
	 * approval SLA resumes; the info-gate row is marked `cancelled`.
	 *
	 * Payload: `{ approvalId, infoGateId, cancelledByUserId }`.
	 */
	REQUEST_INFO_CANCELLED = 'request.info_cancelled',
	/**
	 * Catch-all for non-lifecycle mutations. The `priorityTransform` step
	 * hook is the first concrete trigger; future admin form-data edits
	 * (post-Phase 1) reuse this event with a different `change.field`.
	 * Payload: `{ requestId, change: { field, before, after, source } }`.
	 */
	REQUEST_MODIFIED = 'request.modified',

	// Per-step approval — one event per `request_approvals` row mutation.
	REQUEST_APPROVAL_PENDING = 'request.approval_pending',
	REQUEST_APPROVAL_APPROVED = 'request.approval_approved',
	REQUEST_APPROVAL_REJECTED = 'request.approval_rejected',

	/**
	 * Fan-out event emitted by the daily digest cron, one per resolved approver
	 * with at least one pending approval. Consumed by `RequestCenterDigestWorker`
	 * which re-queries the DB to build the per-approver payload and emits
	 * `NOTIFICATION_EMAIL_REQUESTED` with the `pending-approvals-digest`
	 * template. Payload: `{ approverId, runDate }` only — no approval list.
	 */
	REQUEST_CENTER_DIGEST_REQUESTED = 'request_center.digest_requested',

	/**
	 * Fired when the workflow engine or submit path falls back to the
	 * seeded `DEFAULT_APPROVER_EMPLOYEE_ID` because the union of every
	 * ticked approver dimension resolved empty at step entry — e.g. a
	 * submitter without an `employees` row, or a step whose only dimension
	 * is an empty group. Every occurrence is both warn-logged and emitted
	 * so ops can triage what would otherwise be a silent "stuck request".
	 */
	APPROVER_RESOLUTION_FALLBACK = 'request.approver_resolution_fallback',

	// ─── Ticket lifecycle Events (post-creation) ──────────────────────────────
	// Emitted by `ticket-service` either at creation time or by the 6-hourly
	// Productive poller. Consumed by `request-center-worker` to drive
	// `approved → completed | failed`. Note: `TICKET_REQUESTED` (above) is the
	// pre-creation event; these are the post-creation surface.
	TICKET_CREATED = 'ticket.created',
	TICKET_REQUEST_FAILED = 'ticket.request_failed',
	TICKET_STATUS_CHANGED = 'ticket.status_changed',
	TICKET_CLOSED = 'ticket.closed',
	/**
	 * Catch-all for non-status Productive field changes detected by the
	 * poller (assignee, due date, priority, etc.). Payload carries
	 * `data.change = { field, before, after }`. One event per changed field
	 * per poll run.
	 */
	TICKET_OTHER_CHANGE = 'ticket.other_change',

	// ─── Comment Events ───────────────────────────────────────────────────────
	// Emitted by `comment-service`. Envelope carries the PARENT entity
	// (typically `request_center_request`) so request-scoped audit reads
	// pick comments up via the indexed `(entity_type, entity_id)` lookup.
	// `data.commentId` + `data.commentEntityType = EventEntityType.COMMENT`
	// preserve the comment's own identity for future comment-scoped queries.
	COMMENT_CREATED = 'comment.created',
	COMMENT_EDITED = 'comment.edited',
	COMMENT_DELETED = 'comment.deleted',
	/**
	 * One event per newly-added employee mention (NOT a list — simplifies
	 * Spec 4's per-recipient notification fan-out to a single `WHERE
	 * data->>'mentionedEmployeeId' = ?` query).
	 */
	COMMENT_MENTIONED = 'comment.mentioned',

	// ─── File Events ──────────────────────────────────────────────────────────
	FILE_UPLOADED = 'file.uploaded',
	FILE_DOWNLOADED = 'file.downloaded',
	FILE_UPDATED = 'file.updated',
	FILE_REBOUND = 'file.rebound',
	FILE_DELETED = 'file.deleted',
	FILE_SHARED = 'file.shared',
	FILE_SHARE_REVOKED = 'file.share_revoked',
	FILE_ACCESS_DENIED = 'file.access_denied',
	FILE_CLEANUP_COMPLETED = 'file.cleanup_completed',
	FILE_PROCESSING_REQUESTED = 'file.processing_requested',
	FILE_PROCESSING_COMPLETED = 'file.processing_completed',
	FILE_PROCESSING_FAILED = 'file.processing_failed',
}

/**
 * Type-safe union of all event type values.
 * Use this when you need the string literal type instead of the enum.
 */
export type EventTypeValue = `${EventType}`

/**
 * Entity types for event + comment linking.
 *
 * Used in the `events` table (`entity_type` + `entity_id`) to link events
 * to specific domain entities, and in the `comments` table (same column
 * names) so the comments primitive identifies its parent. Enables indexed
 * timeline queries like: "every event for request X" or "every comment
 * on ticket Y".
 *
 * **Single source of truth** — every producer / consumer uses an enum
 * member, never a hand-typed string. CI's `lint:isolation` check enforces
 * this on primitives that must stay generic (per ADR-0011).
 *
 * Adding a new domain entity = one new line here. The audit reader and
 * the comments adapter pick it up automatically.
 */
export enum EventEntityType {
	IDM_REQUEST = 'idm_request',
	EMPLOYEE = 'employee',
	ENTRA_DIRECTORY = 'entra_directory',
	USER = 'user',
	PROVIDER = 'provider',
	CLIENT = 'client',
	PROJECT = 'project',
	TICKET = 'ticket',
	FILE = 'file',
	// Spec 3 additions — comments primitive + request center audit envelope.
	REQUEST_CENTER_REQUEST = 'request_center_request',
	COMMENT = 'comment',
}
